import React, { useEffect, useState, useCallback } from 'react';
import {
  Table,
  Tag,
  Button,
  Form,
  Input,
  Select,
  DatePicker,
  Space,
  Typography,
} from 'antd';
import type { TableColumnsType } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import Papa from 'papaparse';
import {
  getAuditLog,
  type AuditLogEntry,
  type AuditLogParams,
} from '../../services/adminKycApiService';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

type FilterForm = {
  submissionId: string;
  actorType: string;
  action: string;
  dateRange: [Dayjs, Dayjs] | null;
};

const ACTOR_COLORS: Record<string, string> = {
  SYSTEM: 'default',
  ADMIN: 'blue',
  PROVIDER: 'green',
};

/** Expandable JSON detail cell */
function ExpandableDetail({ value }: { value: Record<string, unknown> | null }) {
  const [expanded, setExpanded] = useState(false);
  if (!value || Object.keys(value).length === 0) {
    return <span style={{ color: '#aaa' }}>—</span>;
  }
  const preview = Object.keys(value).slice(0, 2).join(', ');
  return (
    <div>
      <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setExpanded(!expanded)}>
        {expanded ? 'Collapse' : preview}
      </Button>
      {expanded && (
        <pre style={{
          fontSize: 11,
          background: '#f5f5f5',
          padding: 8,
          borderRadius: 4,
          maxHeight: 200,
          overflow: 'auto',
          margin: '4px 0 0',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all',
        }}>
          {JSON.stringify(value, null, 2)}
        </pre>
      )}
    </div>
  );
}

/** KYC audit log page with filtering, pagination, and CSV export. */
export function AdminAuditLogPage() {
  const [form] = Form.useForm<FilterForm>();
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [activeFilters, setActiveFilters] = useState<AuditLogParams>({});

  const buildParams = (f: Partial<FilterForm>, p = 0, size = 20): AuditLogParams => {
    const params: AuditLogParams = { page: p, size };
    if (f.submissionId?.trim()) params.submissionId = f.submissionId.trim();
    if (f.actorType) params.actorType = f.actorType;
    if (f.action?.trim()) params.action = f.action.trim();
    if (f.dateRange) {
      params.startDate = f.dateRange[0].toISOString();
      params.endDate = f.dateRange[1].toISOString();
    }
    return params;
  };

  const load = useCallback(async (params: AuditLogParams) => {
    setLoading(true);
    try {
      const res = await getAuditLog(params);
      setEntries(res.content);
      setTotal(res.totalElements);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load({ page: 0, size: 20 });
  }, [load]);

  const handleSearch = () => {
    const vals = form.getFieldsValue();
    const params = buildParams(vals, 0);
    setActiveFilters(params);
    setPage(0);
    void load(params);
  };

  const handleReset = () => {
    form.resetFields();
    const params: AuditLogParams = { page: 0, size: 20 };
    setActiveFilters(params);
    setPage(0);
    void load(params);
  };

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const res = await getAuditLog({ ...activeFilters, page: 0, size: 1000 });
      const csv = Papa.unparse(
        res.content.map((e) => ({
          id: e.id,
          submissionId: e.submissionId,
          actorType: e.actorType,
          actorId: e.actorId ?? '',
          action: e.action,
          detail: e.detail ? JSON.stringify(e.detail) : '',
          createdAt: e.createdAt,
        }))
      );
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `kyc-audit-log-${dayjs().format('YYYYMMDD')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExportLoading(false);
    }
  };

  const columns: TableColumnsType<AuditLogEntry> = [
    {
      title: 'Timestamp',
      dataIndex: 'createdAt',
      sorter: true,
      render: (v: string) => dayjs(v).format('DD/MM/YYYY HH:mm:ss'),
      width: 170,
    },
    {
      title: 'Actor type',
      dataIndex: 'actorType',
      width: 110,
      render: (t: string) => (
        <Tag color={ACTOR_COLORS[t] ?? 'default'}>{t}</Tag>
      ),
    },
    {
      title: 'Actor ID',
      dataIndex: 'actorId',
      width: 130,
      render: (id: string | null) =>
        id ? (
          <Text code style={{ fontSize: 11 }}>{id.substring(0, 8)}…</Text>
        ) : (
          <span style={{ color: '#aaa' }}>—</span>
        ),
    },
    {
      title: 'Action',
      dataIndex: 'action',
    },
    {
      title: 'Detail',
      dataIndex: 'detail',
      render: (v: Record<string, unknown> | null) => <ExpandableDetail value={v} />,
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>
          Audit Log
        </Title>
        <Button loading={exportLoading} onClick={handleExport}>
          Export CSV
        </Button>
      </div>

      {/* Filters */}
      <Form
        form={form}
        layout="inline"
        style={{ marginBottom: 16, flexWrap: 'wrap', gap: 8 }}
      >
        <Form.Item name="submissionId" label="Submission ID">
          <Input placeholder="Any" allowClear style={{ width: 200 }} />
        </Form.Item>
        <Form.Item name="actorType" label="Actor type">
          <Select placeholder="All" allowClear style={{ width: 130 }}>
            <Select.Option value="SYSTEM">SYSTEM</Select.Option>
            <Select.Option value="ADMIN">ADMIN</Select.Option>
            <Select.Option value="PROVIDER">PROVIDER</Select.Option>
          </Select>
        </Form.Item>
        <Form.Item name="action" label="Action">
          <Input placeholder="Any" allowClear style={{ width: 160 }} />
        </Form.Item>
        <Form.Item name="dateRange" label="Date range">
          <RangePicker format="DD/MM/YYYY" />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button type="primary" onClick={handleSearch}>
              Search
            </Button>
            <Button onClick={handleReset}>Reset</Button>
          </Space>
        </Form.Item>
      </Form>

      <Table
        columns={columns}
        dataSource={entries}
        rowKey="id"
        loading={loading}
        pagination={{
          current: page + 1,
          total,
          pageSize: 20,
          onChange: (p) => {
            const params = { ...activeFilters, page: p - 1, size: 20 };
            setPage(p - 1);
            void load(params);
          },
          showTotal: (t) => `${t} entries`,
        }}
      />
    </div>
  );
}

export default AdminAuditLogPage;
